import type { Expression, ObservedView, OptionCost } from "./types";

/**
 * Presentation helpers. Everything here turns numbers the player could realistically know into
 * the rounded words a real managing partner would use (19 §1.3, §4.2 cost preview rules).
 */

const DAY_NAMES: Record<string, string> = {
  mon: "Monday", tue: "Tuesday", wed: "Wednesday", thu: "Thursday", fri: "Friday", sat: "Saturday", sun: "Sunday",
};
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function dayName(day: string): string {
  return DAY_NAMES[day.toLowerCase()] ?? day;
}

export function halfName(half: "am" | "pm"): string {
  return half === "am" ? "Morning" : "Afternoon";
}

/** "2026-02-23" → "23 Feb" (no Date parsing, so no time-zone drift). */
export function formatDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso);
  if (!m) return iso;
  const month = MONTHS[Number(m[2]) - 1] ?? m[2];
  return `${Number(m[3])} ${month}`;
}

export function formatClock(clock: ObservedView["clock"]): string {
  return `${dayName(clock.day)} ${formatDate(clock.date)} · ${halfName(clock.half)}`;
}

/** Hours as the HUD shows them: one decimal place, trailing ".0" dropped. 0.58000001 → "0.6 h". */
export function formatHours(hours: number): string {
  const rounded = Math.round(hours * 10) / 10;
  return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)} h`;
}

/**
 * A rounded time estimate for an option (19 §4.2): "~10 min", "~1 h", "~1.5 h".
 * Under an hour rounds to the nearest 5 minutes; an hour or more to the nearest half hour.
 * Returns null when the option carries no time cost the player would notice.
 */
export function formatTimeEstimate(hours: number | undefined): string | null {
  if (hours === undefined || hours <= 0) return null;
  const minutes = hours * 60;
  if (minutes < 57.5) return `~${Math.max(5, Math.round(minutes / 5) * 5)} min`;
  const halfHours = Math.round(hours * 2) / 2;
  return `~${Number.isInteger(halfHours) ? halfHours.toFixed(0) : halfHours.toFixed(1)} h`;
}

/** Political capital is shown as a band, never a number (19 §4.2). */
export type PcBand = "some" | "significant";
export const PC_SIGNIFICANT_AT = 5;
export function pcBand(pc: number | undefined): PcBand | null {
  if (pc === undefined || pc <= 0) return null;
  return pc >= PC_SIGNIFICANT_AT ? "significant" : "some";
}

/** Energy cost is shown as a word, never a number (19 §4.2). */
export function enWord(en: number | undefined): string | null {
  if (en === undefined || en <= 0) return null;
  return "Draining";
}

export interface CostParts {
  time: string | null;
  kind: "in" | "on";
  kindLabel: "IN" | "ON";
  pc: PcBand | null;
  en: string | null;
}

export function costParts(cost: OptionCost): CostParts {
  return {
    time: formatTimeEstimate(cost.hours),
    kind: cost.kind,
    kindLabel: cost.kind === "on" ? "ON" : "IN",
    pc: pcBand(cost.pc),
    en: enWord(cost.en),
  };
}

/** One plain sentence for screen readers, e.g. "About 20 minutes, IN time, some political cost." */
export function costSentence(cost: OptionCost): string {
  const p = costParts(cost);
  const bits: string[] = [];
  if (p.time) {
    const [n = "", unit] = p.time.replace("~", "").split(" ");
    const word = unit === "min" ? "minutes" : n === "1" ? "hour" : "hours";
    bits.push(`about ${n} ${word}`);
  }
  bits.push(p.kind === "on" ? "time working ON the firm" : "time working IN the firm");
  if (p.pc) bits.push(`${p.pc} political cost`);
  if (p.en) bits.push("draining");
  const s = bits.join(", ");
  return s.charAt(0).toUpperCase() + s.slice(1) + ".";
}

/** EN word; changes at the 40 threshold that matters (19 §3.1, 03). */
export const EN_LOW_THRESHOLD = 40;
export function energyWord(en: number): string {
  if (en >= 75) return "Fresh";
  if (en >= 55) return "Steady";
  if (en >= EN_LOW_THRESHOLD) return "Stretched";
  return "Running on empty";
}

/** How an observed expression reads in plain words (19 §1.7). Never names hidden state. */
export const EXPRESSION_WORDS: Record<Expression, string> = {
  neutral: "composed",
  pleased: "smiling",
  concerned: "looks concerned",
  tired: "looks tired",
  irritated: "looks irritated",
  upset: "looks upset",
  guarded: "looks guarded",
};

export function firstName(name: string): string {
  return name.split(/\s+/)[0] ?? name;
}

/** 1 → "1st", 2 → "2nd", 11 → "11th", 23 → "23rd". */
export function ordinal(n: number): string {
  const mod100 = n % 100;
  if (mod100 >= 11 && mod100 <= 13) return `${n}th`;
  switch (n % 10) {
    case 1: return `${n}st`;
    case 2: return `${n}nd`;
    case 3: return `${n}rd`;
    default: return `${n}th`;
  }
}

/** Perception-gated repeat marker (20 §2): a count of what the player has experienced, never advice. */
export function repeatMarker(count: number): string {
  return `${ordinal(count)} time this month`;
}

/** Rounded weekly saving, e.g. 1.25 → "about 1.5 h a week". */
export function formatWeeklySaving(hours: number): string {
  const r = Math.round(hours * 2) / 2;
  if (r <= 0) return "under half an hour a week";
  return `about ${Number.isInteger(r) ? r.toFixed(0) : r.toFixed(1)} h a week`;
}
